import type {
	FeatureEntity,
	FeatureState,
	MeteredFeatureState,
	MeteredPerEntityFeatureState,
	MeteredPerPeriodFeatureState,
	MeteredPerPeriodWithBalanceFeatureState,
} from "@src/gql/graphql";
import { SupportedFeature } from "@src/gql/graphql";

import { useMemo } from "react";
import type { ReactNode } from "react";

/**
 * Props for FeatureGate when using children
 */
type ChildrenFeatureGateProps = {
	/**
	 * Content to render if the feature is enabled
	 */
	children: ReactNode;
	/**
	 * Optional content to render if the feature is disabled
	 * If not provided, null will be rendered
	 */
	fallbackComponent?: ReactNode;
	render?: never;
};

/**
 * Props for FeatureGate when using render prop pattern
 */
type RenderPropFeatureGateProps = {
	children?: never;
	fallbackComponent?: never;
	/**
	 * Function that receives the enabled state and returns content to render
	 */
	render: (props: {
		enabled: boolean;
		stats?: FeatureStats;
		details?: FeatureGateResult[];
	}) => ReactNode;
};

export type FeatureGateProps =
	| ChildrenFeatureGateProps
	| RenderPropFeatureGateProps;

export type FeatureGateResult = ResolvedFeatureGateConfig & {
	enabled: boolean;
	stats?: FeatureStats;
};

export type FeatureGateConfig = {
	feature: SupportedFeature;
} & (
	| { scope: "session" }
	| {
			scope: "entity";
			entity?: {
				id: string;
				type: FeatureEntity;
			};
	  }
);

export type FeatureGateInput =
	| SupportedFeature
	| FeatureGateConfig
	| Array<SupportedFeature | FeatureGateConfig>;

export type ResolvedFeatureGateConfig = {
	feature: SupportedFeature;
	scope: "session" | "entity";
	entity?: {
		id: string;
		type: FeatureEntity;
	};
};

export type FeatureStats = {
	available: boolean;
	current: number;
	quota: number;
	period?: string;
	periodReset?: string;
	balance?: number;
	balanceUsedThisPeriod?: number;
	featureAvailable?: boolean;
};

function isCreditsBudgetFeatureState(featureState: {
	feature: SupportedFeature;
}): featureState is MeteredPerPeriodWithBalanceFeatureState {
	return featureState.feature === SupportedFeature.CreditsBudget;
}

export function useNormalizedFeatures(
	feature?: FeatureGateInput,
	defaultScope: "session" | "entity" = "session",
	escapedEntity?: {
		id: string;
		type: FeatureEntity;
	},
): ResolvedFeatureGateConfig[] {
	return useMemo(() => {
		if (!feature) {
			return [];
		}

		const toConfig = (
			item: SupportedFeature | FeatureGateConfig,
		): ResolvedFeatureGateConfig => {
			if (typeof item === "string") {
				if (defaultScope === "entity" && escapedEntity) {
					return {
						feature: item,
						scope: "entity",
						entity: escapedEntity,
					};
				}

				return { feature: item, scope: defaultScope };
			}

			if (item.scope === "entity") {
				return {
					feature: item.feature,
					scope: "entity",
					entity: item.entity,
				};
			}

			return { feature: item.feature, scope: "session" };
		};

		const list = Array.isArray(feature) ? feature : [feature];
		return list.map(toConfig);
	}, [feature, defaultScope, escapedEntity]);
}

/**
 * Check if a feature is enabled based on the subscription
 *
 * @param featureState
 * @returns true if the feature is enabled, false otherwise
 */
export function isFeatureEnabled(
	featureState?:
		| FeatureState
		| MeteredFeatureState
		| MeteredPerEntityFeatureState
		| MeteredPerPeriodFeatureState
		| MeteredPerPeriodWithBalanceFeatureState,
): { enabled: boolean; stats?: FeatureStats } {
	if (!featureState) {
		return { enabled: false, stats: undefined };
	}

	if ("quota" in featureState) {
		if (isCreditsBudgetFeatureState(featureState)) {
			return {
				enabled: featureState.available,
				stats: {
					available: featureState.available,
					current: featureState.current,
					quota: featureState.quota,
					period: String(featureState.period),
					periodReset: String(featureState.periodReset),
					balance: featureState.balance,
					balanceUsedThisPeriod: featureState.balanceUsedThisPeriod,
					featureAvailable: featureState.featureAvailable,
				},
			};
		}

		const periodStats =
			"periodReset" in featureState
				? {
						period: String(featureState.period),
						periodReset: String(featureState.periodReset),
					}
				: {};

		return {
			enabled:
				featureState.available && featureState.current <= featureState.quota,
			stats: {
				available: featureState.available,
				current: featureState.current,
				quota: featureState.quota,
				...periodStats,
			},
		};
	}

	return {
		enabled: featureState.available,
		stats: undefined,
	};
}

export function updateMeteredStats(
	stats: FeatureStats,
	update: {
		delta: number;
		quota?: number;
	},
): FeatureStats {
	const nextCurrent = Math.max(0, stats.current + update.delta);
	const quota = update.quota ?? stats.quota;
	return {
		available: nextCurrent <= quota,
		current: nextCurrent,
		quota,
	};
}
